import {getResource} from "./common"
import {AuditLogEventChanges} from "../components/AuditLogEventsView/AuditLogEventsView"

export type AuditLogResource =
	| "card"
	| "deposit-product"
	| "customer"
	| "account"
	| "account-hold"
	| "application"
	| "wallet-terms"
	| "IncomingACH"
	| "IncomingWire"
	| "IncomingRealTimePayment"
	| "WirePayment"
	| "AchPayment"
	| "FedWirePayment"
	| "productApplication"
	| "account-config-override"

export type AuditLogSubResource =
	| "beneficialOwner"
	| "document"
	| "productApplicationOfficer"
	| "productApplicationBeneficialOwner"
	| "productApplicationDocument"
	| "customerOfficer"
	| "customerBeneficialOwner"

const subResourceDisplayName: Record<AuditLogSubResource, string> = {
	beneficialOwner: "Beneficial Owner",
	document: "Document",
	productApplicationOfficer: "Officer",
	productApplicationBeneficialOwner: "Beneficial Owner",
	productApplicationDocument: "Document",
	customerOfficer: "Officer",
	customerBeneficialOwner: "Beneficial Owner",
}

type AuditLogAttributes = {
	createdAt: string
	actorUserId: string
	actorUserRole: string
	actorUserType: string
	actorUserName: string
	actorBankId?: string
	actorOrgId?: string
	resourceType: AuditLogResource | AuditLogSubResource
	resourceId: string
	intent: string
	changes?: AuditLogEventChanges[]
	actionTimestamp: string
	fileAttachments?: {
		fileAttachment: {
			id: string
		}
	}[]
}

export type AuditLog = {
	type: string
	id: string
	attributes: AuditLogAttributes
	relationships: {
		banks: {
			data: [
				{
					type: string
					id: string
				}
			]
		}
		org: {
			data: {
				type: string
				id: string
			}
		}
	}
}

export async function findApplicationAuditLogs(accessToken: string, offset: number, limit: number, resourceId: string) {
	const query = {
		page: {
			limit,
			offset,
		},
	}
	return await getResource<Array<AuditLog>>(`applications/${resourceId}/audit-log`, accessToken, query)
}

export async function findProductApplicationAuditLogs(
	accessToken: string,
	offset: number,
	limit: number,
	resourceId: string
) {
	const query = {
		page: {
			limit,
			offset,
		},
	}
	return await getResource<Array<AuditLog>>(`product-applications/${resourceId}/audit-logs`, accessToken, query)
}

export async function findCustomerAuditLogs(accessToken: string, offset: number, limit: number, resourceId: string) {
	const query = {
		page: {
			limit,
			offset,
		},
	}
	return await getResource<Array<AuditLog>>(`customers/${resourceId}/audit-logs`, accessToken, query)
}

export async function findAuditLogs(
	accessToken: string,
	offset: number,
	limit: number,
	resourceIds: string[],
	resourceTypes: AuditLogResource[]
) {
	const query = {
		page: {
			limit,
			offset,
		},
		filter: {
			resourceIds: resourceIds,
			resourceTypes: resourceTypes,
		},
	}

	return await getResource<Array<AuditLog>>("audit-log", accessToken, query)
}

export async function findAuditLogsByRelatedAccount(
	accessToken: string,
	offset: number,
	limit: number,
	relatedAccountId: string,
	resourceTypes: AuditLogResource[]
) {
	const query = {
		page: {
			limit,
			offset,
		},
		filter: {
			relatedAccounts: [relatedAccountId],
			resourceTypes: resourceTypes,
		},
	}

	return await getResource<Array<AuditLog>>("audit-log", accessToken, query)
}

const isSubResource = (type: AuditLogResource | AuditLogSubResource): type is AuditLogSubResource =>
	type in subResourceDisplayName

const enrichWithSubResourceIntent = (auditLog: AuditLog): AuditLog => {
	const intent = auditLog.attributes.intent
	const resourceId = auditLog.attributes.resourceId
	const auditLogResourceType = auditLog.attributes.resourceType
	const displayName = isSubResource(auditLogResourceType)
		? subResourceDisplayName[auditLogResourceType]
		: auditLogResourceType
	return {
		...auditLog,
		attributes: {
			...auditLog.attributes,
			intent: `${intent} (${displayName} Id ${resourceId})`,
		},
	}
}

export const enrichIntentWithSubResourceId = (
	auditLogs: AuditLog[],
	showSubResourcesId: boolean,
	resourceType: AuditLogResource
) => {
	if (!showSubResourcesId) {
		return auditLogs
	}
	return auditLogs.map((auditLog) => {
		if (resourceType !== auditLog.attributes.resourceType) {
			return enrichWithSubResourceIntent(auditLog)
		} else {
			return auditLog
		}
	})
}
